import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { getClassrooms, createExercise, getTemplates, getSharedTemplates, createExerciseFromTemplate, createTemplate, getTeachers, deleteTemplate } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Zap, Loader2, Search, CheckCircle, AlertCircle, 
  FolderOpen, Plus, BookOpen, Clock, Target, Sparkles, FileText,
  Share2, Users, Globe, Save, X, User, Keyboard, BookOpenCheck, Trash2, Printer
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateExercise = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClassroom = searchParams.get('classroom');
  const initialTab = searchParams.get('tab');
  
  const [classrooms, setClassrooms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sharedTemplates, setSharedTemplates] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [standardFound, setStandardFound] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab === 'template' ? 'template' : 'new');
  const [templateTab, setTemplateTab] = useState('my'); // 'my' or 'shared'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Save & Share state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [shareSchoolWide, setShareSchoolWide] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [lastCreatedExercise, setLastCreatedExercise] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    objectives: [{ code: '', description: '' }],  // Array of objectives (up to 5)
    grade_level: '8',
    duration_minutes: 5,
    target_wpm: 30,
    num_questions: 5,  // Number of quiz questions (1-20)
    exercise_type: 'typing',  // 'typing' (typing + quiz) or 'reading' (reading quiz only)
    source_type: 'ai_generated',  // 'ai_generated' or 'custom_text'
    custom_passage: '',
    use_exact_text: false  // When true, skip AI adaptation and use exact pasted text
  });
  
  // For standard lookup - track the currently editing objective index
  const [editingObjectiveIndex, setEditingObjectiveIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classroomsRes, templatesRes, sharedRes, teachersRes] = await Promise.all([
          getClassrooms(),
          getTemplates(),
          getSharedTemplates(),
          getTeachers()
        ]);
        setClassrooms(classroomsRes.data);
        setTemplates(templatesRes.data);
        setSharedTemplates(sharedRes.data);
        setTeachers(teachersRes.data);
        
        // Pre-select classroom if provided
        if (preselectedClassroom) {
          setSelectedClassrooms([preselectedClassroom]);
          const classroom = classroomsRes.data.find(c => c.classroom_id === preselectedClassroom);
          if (classroom) {
            setForm(f => ({ ...f, grade_level: classroom.grade_level }));
          }
        }
      } catch {
        toast.error('Failed to load data');
        navigate('/teacher');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [preselectedClassroom, navigate]);

  const lookupStandard = async (code) => {
    if (!code || code.length < 2) {
      setStandardFound(null);
      setSuggestions([]);
      return;
    }
    
    setLookingUp(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/standards/lookup/${encodeURIComponent(code)}`);
      if (res.data.suggestions) {
        setSuggestions(res.data.suggestions);
        setStandardFound(null);
      } else {
        setStandardFound(res.data);
        setSuggestions([]);
        // Update the specific objective being edited
        updateObjective(editingObjectiveIndex, res.data.code, res.data.description);
      }
    } catch {
      setStandardFound(null);
      setSuggestions([]);
    } finally {
      setLookingUp(false);
    }
  };

  const handleCodeChange = (e, index = 0) => {
    const code = e.target.value.toUpperCase();
    setEditingObjectiveIndex(index);
    updateObjective(index, code, form.objectives[index]?.description || '');
    
    clearTimeout(window.standardLookupTimeout);
    window.standardLookupTimeout = setTimeout(() => {
      lookupStandard(code);
    }, 300);
  };

  const updateObjective = (index, code, description) => {
    const newObjectives = [...form.objectives];
    newObjectives[index] = { code, description };
    setForm({ ...form, objectives: newObjectives });
  };

  const addObjective = () => {
    if (form.objectives.length < 5) {
      setForm({ ...form, objectives: [...form.objectives, { code: '', description: '' }] });
    }
  };

  const removeObjective = (index) => {
    if (form.objectives.length > 1) {
      const newObjectives = form.objectives.filter((_, i) => i !== index);
      setForm({ ...form, objectives: newObjectives });
      // Reset standard found when removing
      setStandardFound(null);
      setSuggestions([]);
    }
  };

  const selectSuggestion = (suggestion) => {
    updateObjective(editingObjectiveIndex, suggestion.code, suggestion.description);
    setStandardFound(suggestion);
    setSuggestions([]);
  };

  const toggleClassroom = (classroomId) => {
    setSelectedClassrooms(prev => 
      prev.includes(classroomId) 
        ? prev.filter(id => id !== classroomId)
        : [...prev, classroomId]
    );
  };

  const selectAllClassrooms = () => {
    if (selectedClassrooms.length === classrooms.length) {
      setSelectedClassrooms([]);
    } else {
      setSelectedClassrooms(classrooms.map(c => c.classroom_id));
    }
  };

  const handleSubmitNew = async () => {
    if (selectedClassrooms.length === 0) {
      toast.error('Please select at least one classroom');
      return;
    }
    if (!form.title) {
      toast.error('Please enter an exercise title');
      return;
    }
    // Validate at least one objective is complete
    const validObjectives = form.objectives.filter(obj => obj.code && obj.description);
    if (validObjectives.length === 0) {
      toast.error('Please add at least one ELA objective');
      return;
    }
    // Validate custom text if that option is selected
    if (form.source_type === 'custom_text' && (!form.custom_passage || form.custom_passage.trim().length < 50)) {
      toast.error('Please paste at least 50 characters of text for your custom passage');
      return;
    }
    
    setCreating(true);
    try {
      const response = await createExercise({
        classroom_ids: selectedClassrooms,
        title: form.title,
        objectives: validObjectives,
        grade_level: form.grade_level,
        duration_minutes: form.duration_minutes,
        target_wpm: form.target_wpm,
        num_questions: form.num_questions,
        exercise_type: form.exercise_type,
        source_type: form.source_type,
        custom_passage: form.source_type === 'custom_text' ? form.custom_passage : null,
        use_exact_text: form.source_type === 'custom_text' ? form.use_exact_text : false
      });
      
      // Store the created exercise for save & share
      const exercise = response.data.exercises ? response.data.exercises[0] : response.data;
      setLastCreatedExercise(exercise);
      
      const msg = selectedClassrooms.length === 1 
        ? 'Exercise created successfully!' 
        : `Exercise assigned to ${selectedClassrooms.length} classes!`;
      toast.success(msg);
      
      // Show save & share modal
      setShowSaveModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create exercise');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveToLibrary = async () => {
    if (!lastCreatedExercise) return;
    
    setSaving(true);
    try {
      await createTemplate({
        title: lastCreatedExercise.title,
        objectives: lastCreatedExercise.objectives || [
          { code: lastCreatedExercise.objective_code, description: lastCreatedExercise.objective_description }
        ],
        grade_level: lastCreatedExercise.grade_level,
        duration_minutes: lastCreatedExercise.duration_minutes,
        target_wpm: lastCreatedExercise.target_wpm,
        num_questions: lastCreatedExercise.num_questions || lastCreatedExercise.quiz_questions?.length || 5,
        exercise_type: lastCreatedExercise.exercise_type || 'typing',
        passage: lastCreatedExercise.passage,
        quiz_questions: lastCreatedExercise.quiz_questions,
        share_school_wide: shareSchoolWide,
        shared_with_teachers: selectedTeachers
      });
      
      const shareMsg = shareSchoolWide 
        ? 'Saved and shared school-wide!' 
        : selectedTeachers.length > 0 
          ? `Saved and shared with ${selectedTeachers.length} teacher(s)!`
          : 'Saved to your library!';
      toast.success(shareMsg);
      setShowSaveModal(false);
      navigate('/teacher');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const toggleTeacherSelection = (teacherId) => {
    setSelectedTeachers(prev => 
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const handleSubmitFromTemplate = async () => {
    if (selectedClassrooms.length === 0) {
      toast.error('Please select at least one classroom');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    
    setCreating(true);
    try {
      await createExerciseFromTemplate({
        classroom_ids: selectedClassrooms,
        template_id: selectedTemplate.template_id,
        title: form.title || null
      });
      const msg = selectedClassrooms.length === 1 
        ? 'Exercise created from template!' 
        : `Exercise assigned to ${selectedClassrooms.length} classes!`;
      toast.success(msg);
      navigate('/teacher');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create exercise');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    setDeleting(true);
    try {
      await deleteTemplate(templateId);
      // Remove from local state
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
      // Clear selection if deleted template was selected
      if (selectedTemplate?.template_id === templateId) {
        setSelectedTemplate(null);
      }
      toast.success('Template deleted from library');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrintTemplate = (template) => {
    // Open a new window with print-friendly content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }

    const objectiveCodes = template.objectives?.length > 0 
      ? template.objectives.map(o => o.code).join(', ')
      : template.objective_code || '';

    const objectiveDescriptions = template.objectives?.length > 0
      ? template.objectives.map(o => `${o.code}: ${o.description}`).join('<br>')
      : `${template.objective_code}: ${template.objective_description || ''}`;

    // Format quiz questions
    let questionsHtml = '';
    if (template.quiz_questions && template.quiz_questions.length > 0) {
      questionsHtml = template.quiz_questions.map((q, idx) => `
        <div class="question">
          <div class="question-header">
            <p class="question-num"><strong>${idx + 1}.</strong> ${q.question}</p>
            ${q.objective_code ? `<span class="objective-badge">${q.objective_code}</span>` : ''}
          </div>
          <div class="options">
            ${q.options.map(opt => `<p class="option">${opt}</p>`).join('')}
          </div>
        </div>
      `).join('');
    }

    // Answer key with objectives and hints
    let answerKeyHtml = '';
    if (template.quiz_questions && template.quiz_questions.length > 0) {
      answerKeyHtml = `
        <div class="answer-key">
          <h3>Answer Key</h3>
          <table class="answer-table">
            <tr>
              <th>#</th>
              <th>Answer</th>
              <th>Objective</th>
              <th>Hint/Explanation</th>
            </tr>
            ${template.quiz_questions.map((q, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td><strong>${q.correct_answer}</strong></td>
                <td>${q.objective_code || '-'}</td>
                <td class="hint-cell">${(q.hint || q.explanation || '-').substring(0, 100)}${(q.hint || q.explanation || '').length > 100 ? '...' : ''}</td>
              </tr>
            `).join('')}
          </table>
        </div>
      `;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${template.title} - LitBlitz</title>
        <style>
          * { box-sizing: border-box; }
          body { 
            font-family: 'Times New Roman', Times, serif; 
            max-width: 8in; 
            margin: 0.5in auto; 
            padding: 0 0.25in;
            font-size: 12pt;
            line-height: 1.5;
            color: #000;
          }
          h1 { 
            font-size: 18pt; 
            margin-bottom: 0.25in;
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: 0.1in;
          }
          .header-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.25in;
            font-size: 10pt;
            border-bottom: 1px solid #ccc;
            padding-bottom: 0.1in;
          }
          .name-line {
            margin: 0.2in 0;
            font-size: 11pt;
          }
          .name-line span {
            display: inline-block;
            border-bottom: 1px solid #000;
            min-width: 2.5in;
            margin-left: 0.1in;
          }
          .objectives {
            background: #f5f5f5;
            padding: 0.15in;
            margin-bottom: 0.25in;
            border-radius: 4px;
            font-size: 10pt;
          }
          .objectives strong { font-size: 11pt; }
          h2 { 
            font-size: 14pt; 
            margin: 0.3in 0 0.15in 0;
            border-bottom: 1px solid #ccc;
            padding-bottom: 0.05in;
          }
          .passage { 
            text-align: justify;
            margin-bottom: 0.3in;
            padding: 0.15in;
            border: 1px solid #ddd;
            background: #fafafa;
          }
          .question {
            margin-bottom: 0.25in;
            page-break-inside: avoid;
          }
          .question-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 0.1in;
          }
          .question-num {
            margin-bottom: 0.1in;
            flex: 1;
          }
          .objective-badge {
            background: #e0e7ff;
            color: #4338ca;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 9pt;
            font-family: monospace;
            flex-shrink: 0;
            margin-left: 0.1in;
          }
          .options {
            margin-left: 0.3in;
          }
          .option {
            margin: 0.05in 0;
          }
          .answer-key {
            margin-top: 0.5in;
            padding-top: 0.2in;
            border-top: 2px dashed #ccc;
            page-break-before: always;
          }
          .answer-key h3 {
            font-size: 12pt;
            margin-bottom: 0.1in;
          }
          .answer-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10pt;
            margin-top: 0.1in;
          }
          .answer-table th, .answer-table td {
            border: 1px solid #ccc;
            padding: 0.08in;
            text-align: left;
            vertical-align: top;
          }
          .answer-table th {
            background: #f0f0f0;
            font-weight: bold;
          }
          .hint-cell {
            font-size: 9pt;
            color: #555;
          }
          @media print {
            body { margin: 0; padding: 0.25in; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${template.title}</h1>
        
        <div class="header-info">
          <span>Grade ${template.grade_level} | ${template.exercise_type === 'reading' ? 'Reading Quiz' : 'Typing Exercise'}</span>
          <span>Objectives: ${objectiveCodes}</span>
        </div>
        
        <div class="name-line">
          Name: <span></span> &nbsp;&nbsp;&nbsp; Date: <span style="min-width: 1.5in;"></span>
        </div>
        
        <div class="objectives">
          <strong>Learning Objectives:</strong><br>
          ${objectiveDescriptions}
        </div>
        
        <h2>Reading Passage</h2>
        <div class="passage">
          ${template.passage || 'No passage available'}
        </div>
        
        <h2>Comprehension Questions</h2>
        ${questionsHtml || '<p>No questions available</p>'}
        
        ${answerKeyHtml}
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/teacher')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Create Exercise</h1>
            <p className="text-text-secondary text-sm">Assign to one or more classes</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Classroom Selection */}
          <div className="lg:col-span-1">
            <Card className="card-glass p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Select Classes
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllClassrooms}
                  className="text-xs"
                  data-testid="select-all-btn"
                >
                  {selectedClassrooms.length === classrooms.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              {classrooms.length === 0 ? (
                <p className="text-text-muted text-sm">No classrooms yet. Create one first.</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {classrooms.map(classroom => (
                    <label
                      key={classroom.classroom_id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedClassrooms.includes(classroom.classroom_id)
                          ? 'bg-primary/20 border border-primary/50'
                          : 'bg-background-surface hover:bg-white/5 border border-transparent'
                      }`}
                      data-testid={`classroom-checkbox-${classroom.classroom_id}`}
                    >
                      <Checkbox
                        checked={selectedClassrooms.includes(classroom.classroom_id)}
                        onCheckedChange={() => toggleClassroom(classroom.classroom_id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{classroom.name}</p>
                        <p className="text-xs text-text-muted">
                          Grade {classroom.grade_level} • {classroom.student_ids?.length || 0} students
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {selectedClassrooms.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-sm text-neon-green">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    {selectedClassrooms.length} class{selectedClassrooms.length > 1 ? 'es' : ''} selected
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Exercise Details */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-background-paper border border-white/10 mb-6">
                <TabsTrigger 
                  value="new" 
                  className="data-[state=active]:bg-primary data-[state=active]:text-black"
                  data-testid="new-exercise-tab"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New
                </TabsTrigger>
                <TabsTrigger 
                  value="template" 
                  className="data-[state=active]:bg-secondary data-[state=active]:text-white"
                  data-testid="template-tab"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  From Library ({templates.length})
                </TabsTrigger>
              </TabsList>

              {/* New Exercise Tab */}
              <TabsContent value="new">
                <Card className="card-glass p-6">
                  <div className="space-y-6">
                    {/* Title */}
                    <div>
                      <label className="text-sm text-text-secondary mb-2 block">Exercise Title *</label>
                      <Input
                        className="input-cyber"
                        placeholder="e.g., Story Comprehension Practice"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        data-testid="exercise-title-input"
                      />
                    </div>

                    {/* Exercise Type */}
                    <div>
                      <label className="text-sm text-text-secondary mb-2 block">Exercise Type *</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            form.exercise_type === 'typing'
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 bg-background-surface hover:border-white/20'
                          }`}
                          onClick={() => setForm({ ...form, exercise_type: 'typing' })}
                          data-testid="exercise-type-typing"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${form.exercise_type === 'typing' ? 'bg-primary/20' : 'bg-white/5'}`}>
                              <Keyboard className={`w-5 h-5 ${form.exercise_type === 'typing' ? 'text-primary' : 'text-text-muted'}`} />
                            </div>
                            <div>
                              <p className="font-medium">Typing Lesson</p>
                              <p className="text-xs text-text-muted">Type passage + Quiz</p>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            form.exercise_type === 'reading'
                              ? 'border-secondary bg-secondary/10'
                              : 'border-white/10 bg-background-surface hover:border-white/20'
                          }`}
                          onClick={() => setForm({ ...form, exercise_type: 'reading' })}
                          data-testid="exercise-type-reading"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${form.exercise_type === 'reading' ? 'bg-secondary/20' : 'bg-white/5'}`}>
                              <BookOpenCheck className={`w-5 h-5 ${form.exercise_type === 'reading' ? 'text-secondary' : 'text-text-muted'}`} />
                            </div>
                            <div>
                              <p className="font-medium">Reading Quiz</p>
                              <p className="text-xs text-text-muted">Read passage + Quiz only</p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Grade Level */}
                    <div>
                      <label className="text-sm text-text-secondary mb-2 block">Grade Level *</label>
                      <Select value={form.grade_level} onValueChange={(val) => setForm({ ...form, grade_level: val })}>
                        <SelectTrigger className="input-cyber" data-testid="grade-select">
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                        <SelectContent className="bg-background-paper border-white/10">
                          {GRADE_LEVELS.map(grade => (
                            <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* ELA Standards - Multiple Objectives */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm text-text-secondary">ELA Objectives * (up to 5)</label>
                        {form.objectives.length < 5 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={addObjective}
                            className="text-xs text-primary hover:text-primary/80"
                            data-testid="add-objective-btn"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Objective
                          </Button>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        {form.objectives.map((objective, index) => (
                          <div key={index} className="p-3 bg-background-surface rounded-lg border border-white/10">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs text-text-muted">#{index + 1}</span>
                              {form.objectives.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeObjective(index)}
                                  className="ml-auto text-xs text-red-400 hover:text-red-300 h-6 px-2"
                                  data-testid={`remove-objective-${index}`}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                            <div className="relative">
                              <Input
                                className="input-cyber font-mono pr-10 text-sm"
                                placeholder="e.g., RL.8.1, WHST.6-8.4, W.8.2"
                                value={objective.code}
                                onChange={(e) => handleCodeChange(e, index)}
                                onFocus={() => setEditingObjectiveIndex(index)}
                                data-testid={`objective-code-${index}`}
                              />
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {lookingUp && editingObjectiveIndex === index ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                                ) : objective.code && objective.description ? (
                                  <CheckCircle className="w-4 h-4 text-neon-green" />
                                ) : objective.code?.length >= 2 ? (
                                  <AlertCircle className="w-4 h-4 text-neon-yellow" />
                                ) : (
                                  <Search className="w-4 h-4 text-text-muted" />
                                )}
                              </div>
                            </div>
                            {objective.description && (
                              <p className="text-xs text-text-secondary mt-2 line-clamp-2">
                                {objective.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Suggestions dropdown - only show when editing */}
                      {suggestions.length > 0 && (
                        <div className="mt-2 bg-background-paper border border-white/10 rounded-lg max-h-48 overflow-y-auto">
                          <p className="px-3 py-2 text-xs text-text-muted border-b border-white/10">
                            Did you mean one of these?
                          </p>
                          {suggestions.map((s, idx) => (
                            <button
                              key={idx}
                              className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                              onClick={() => selectSuggestion(s)}
                            >
                              <span className="font-mono text-primary">{s.code}</span>
                              <p className="text-sm text-text-secondary mt-1 line-clamp-2">{s.description}</p>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Summary of valid objectives */}
                      {form.objectives.filter(o => o.code && o.description).length > 0 && (
                        <div className="mt-3 p-3 bg-neon-green/10 border border-neon-green/30 rounded-lg">
                          <p className="text-xs text-neon-green font-medium mb-1">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {form.objectives.filter(o => o.code && o.description).length} objective(s) selected
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {form.objectives.filter(o => o.code && o.description).map((o, i) => (
                              <span key={i} className="text-xs font-mono bg-neon-green/20 px-2 py-0.5 rounded">
                                {o.code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Number of Questions */}
                    <div>
                      <label className="text-sm text-text-secondary mb-2 block">Number of Quiz Questions</label>
                      <Select 
                        value={String(form.num_questions)} 
                        onValueChange={(val) => setForm({ ...form, num_questions: parseInt(val) })}
                      >
                        <SelectTrigger className="input-cyber" data-testid="num-questions-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background-paper border-white/10 max-h-60">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 18, 20].map(num => (
                            <SelectItem key={num} value={String(num)}>{num} questions</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-text-muted mt-1">
                        Questions will be distributed across all selected objectives
                      </p>
                    </div>

                    {/* Passage Source Type */}
                    <div>
                      <label className="text-sm text-text-secondary mb-3 block">Passage Source *</label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            form.source_type === 'ai_generated'
                              ? 'border-primary bg-primary/10'
                              : 'border-white/10 hover:border-white/30 bg-background-surface'
                          }`}
                          onClick={() => setForm({ ...form, source_type: 'ai_generated' })}
                          data-testid="source-ai-btn"
                        >
                          <Sparkles className={`w-6 h-6 mb-2 ${form.source_type === 'ai_generated' ? 'text-primary' : 'text-text-muted'}`} />
                          <p className="font-medium">AI Generated Story</p>
                          <p className="text-xs text-text-muted mt-1">
                            AI creates an original passage based on your settings
                          </p>
                        </button>
                        
                        <button
                          type="button"
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            form.source_type === 'custom_text'
                              ? 'border-secondary bg-secondary/10'
                              : 'border-white/10 hover:border-white/30 bg-background-surface'
                          }`}
                          onClick={() => setForm({ ...form, source_type: 'custom_text' })}
                          data-testid="source-custom-btn"
                        >
                          <FileText className={`w-6 h-6 mb-2 ${form.source_type === 'custom_text' ? 'text-secondary' : 'text-text-muted'}`} />
                          <p className="font-medium">Paste My Own Text</p>
                          <p className="text-xs text-text-muted mt-1">
                            Use historical texts, literature, or your own content
                          </p>
                        </button>
                      </div>
                    </div>

                    {/* Custom Passage Input (shown when custom_text selected) */}
                    {form.source_type === 'custom_text' && (
                      <div className="animate-in slide-in-from-top-2 duration-200 space-y-4">
                        <div>
                          <label className="text-sm text-text-secondary mb-2 block">
                            Paste Your Text *
                            <span className="text-text-muted ml-2">
                              ({form.custom_passage.split(/\s+/).filter(w => w).length} words)
                            </span>
                          </label>
                          <Textarea
                            className="input-cyber min-h-[200px] font-mono text-sm"
                            placeholder="Paste your passage here... (e.g., The Gettysburg Address, excerpts from literature, historical documents, etc.)"
                            value={form.custom_passage}
                            onChange={(e) => setForm({ ...form, custom_passage: e.target.value })}
                            data-testid="custom-passage-input"
                          />
                        </div>

                        {/* Use Exact Text Toggle */}
                        <div className="p-4 bg-background-surface rounded-lg">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                              checked={form.use_exact_text}
                              onCheckedChange={(checked) => setForm({ ...form, use_exact_text: checked })}
                              className="mt-0.5"
                              data-testid="use-exact-text-checkbox"
                            />
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                <FileText className="w-4 h-4 text-secondary" />
                                Use exact text (no adaptation)
                              </p>
                              <p className="text-xs text-text-muted mt-1">
                                Keep your text exactly as written. Best for historical documents, poetry, or when precise wording matters.
                              </p>
                            </div>
                          </label>
                        </div>

                        {/* Info text based on selection */}
                        {form.use_exact_text ? (
                          <p className="text-xs text-neon-green">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            Your exact text will be used. Quiz questions will be generated based on this passage.
                          </p>
                        ) : (
                          <p className="text-xs text-text-muted">
                            <Sparkles className="w-3 h-3 inline mr-1 text-primary" />
                            AI will adapt your text to ~{form.duration_minutes * form.target_wpm} words for Grade {form.grade_level}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Duration & WPM */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm text-text-secondary mb-2 block">Duration</label>
                        <Select 
                          value={String(form.duration_minutes)} 
                          onValueChange={(val) => setForm({ ...form, duration_minutes: parseInt(val) })}
                        >
                          <SelectTrigger className="input-cyber" data-testid="duration-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background-paper border-white/10">
                            {[1, 2, 3, 5, 7, 10, 15].map(min => (
                              <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm text-text-secondary mb-2 block">Target WPM</label>
                        <Select 
                          value={String(form.target_wpm)} 
                          onValueChange={(val) => setForm({ ...form, target_wpm: parseInt(val) })}
                        >
                          <SelectTrigger className="input-cyber" data-testid="wpm-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-background-paper border-white/10">
                            {[15, 20, 25, 30, 35, 40, 50, 60, 80].map(wpm => (
                              <SelectItem key={wpm} value={String(wpm)}>{wpm} WPM</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Preview Info */}
                    <div className="p-4 bg-background-surface rounded-lg">
                      <p className="text-sm text-text-muted">
                        <Target className="w-4 h-4 inline mr-2 text-primary" />
                        {form.source_type === 'ai_generated' 
                          ? `AI will generate ~${form.duration_minutes * form.target_wpm} words of original content`
                          : `AI will adapt your text to ~${form.duration_minutes * form.target_wpm} words for Grade ${form.grade_level}`
                        }
                      </p>
                      <p className="text-xs text-text-muted mt-2">
                        <CheckCircle className="w-3 h-3 inline mr-1 text-neon-green" />
                        5 quiz questions will be generated based on the ELA objective
                      </p>
                    </div>

                    {/* Submit Button */}
                    <Button
                      className="w-full btn-primary py-6"
                      onClick={handleSubmitNew}
                      disabled={creating || selectedClassrooms.length === 0}
                      data-testid="create-exercise-btn"
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {form.source_type === 'ai_generated' ? 'Generating with AI...' : 'Adapting & Generating Quiz...'}
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          Create & Assign to {selectedClassrooms.length} Class{selectedClassrooms.length !== 1 ? 'es' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              </TabsContent>

              {/* Template Tab */}
              <TabsContent value="template">
                <Card className="card-glass p-6">
                  {/* Template Sub-tabs */}
                  <div className="flex gap-2 mb-6">
                    <Button
                      variant={templateTab === 'my' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => { setTemplateTab('my'); setSelectedTemplate(null); }}
                      className={templateTab === 'my' ? 'bg-primary text-black' : ''}
                      data-testid="my-templates-tab"
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      My Library ({templates.length})
                    </Button>
                    <Button
                      variant={templateTab === 'shared' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => { setTemplateTab('shared'); setSelectedTemplate(null); }}
                      className={templateTab === 'shared' ? 'bg-secondary text-white' : ''}
                      data-testid="shared-templates-tab"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Shared With Me ({sharedTemplates.length})
                    </Button>
                  </div>

                  {/* My Templates */}
                  {templateTab === 'my' && (
                    <>
                      {templates.length === 0 ? (
                        <div className="text-center py-12">
                          <FolderOpen className="w-12 h-12 text-text-muted mx-auto mb-4" />
                          <p className="text-text-secondary mb-2">No saved templates yet</p>
                          <p className="text-sm text-text-muted">
                            Create exercises and save them to your library for quick reuse
                          </p>
                        </div>
                      ) : (
                        <TemplateList 
                          templates={templates}
                          selectedTemplate={selectedTemplate}
                          setSelectedTemplate={setSelectedTemplate}
                          form={form}
                          setForm={setForm}
                          creating={creating}
                          selectedClassrooms={selectedClassrooms}
                          handleSubmitFromTemplate={handleSubmitFromTemplate}
                          isShared={false}
                          onDeleteTemplate={handleDeleteTemplate}
                          deleting={deleting}
                          onPrintTemplate={handlePrintTemplate}
                        />
                      )}
                    </>
                  )}

                  {/* Shared Templates */}
                  {templateTab === 'shared' && (
                    <>
                      {sharedTemplates.length === 0 ? (
                        <div className="text-center py-12">
                          <Share2 className="w-12 h-12 text-text-muted mx-auto mb-4" />
                          <p className="text-text-secondary mb-2">No shared templates yet</p>
                          <p className="text-sm text-text-muted">
                            Templates shared by other teachers will appear here
                          </p>
                        </div>
                      ) : (
                        <TemplateList 
                          templates={sharedTemplates}
                          selectedTemplate={selectedTemplate}
                          setSelectedTemplate={setSelectedTemplate}
                          form={form}
                          setForm={setForm}
                          creating={creating}
                          selectedClassrooms={selectedClassrooms}
                          handleSubmitFromTemplate={handleSubmitFromTemplate}
                          isShared={true}
                          onPrintTemplate={handlePrintTemplate}
                        />
                      )}
                    </>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* Save & Share Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="card-glass w-full max-w-lg p-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <Save className="w-5 h-5 text-primary" />
                Save & Share Lesson
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setShowSaveModal(false); navigate('/teacher'); }}
                data-testid="close-save-modal"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-6">
              {/* Exercise Info */}
              <div className="p-4 bg-background-surface rounded-lg">
                <p className="font-medium">{lastCreatedExercise?.title}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {lastCreatedExercise?.objectives?.length > 0 ? (
                    lastCreatedExercise.objectives.map((obj, i) => (
                      <span key={i} className="text-xs font-mono bg-primary/20 text-primary px-2 py-0.5 rounded">
                        {obj.code}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-primary font-mono">{lastCreatedExercise?.objective_code}</span>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-2">
                  Grade {lastCreatedExercise?.grade_level} • {lastCreatedExercise?.duration_minutes}m • {lastCreatedExercise?.target_wpm} WPM
                </p>
              </div>

              {/* School-wide Sharing */}
              <div className="p-4 bg-background-surface rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={shareSchoolWide}
                    onCheckedChange={(checked) => setShareSchoolWide(checked)}
                    data-testid="share-school-wide"
                  />
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-neon-green" />
                    <div>
                      <p className="font-medium">Share School-Wide</p>
                      <p className="text-xs text-text-muted">All teachers can use this lesson</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Teacher Selection */}
              {!shareSchoolWide && teachers.length > 0 && (
                <div>
                  <label className="text-sm text-text-secondary mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Share with specific teachers
                  </label>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto bg-background-surface rounded-lg p-3">
                    {teachers.map(teacher => (
                      <label
                        key={teacher.user_id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                          selectedTeachers.includes(teacher.user_id)
                            ? 'bg-secondary/20 border border-secondary/30'
                            : 'hover:bg-white/5 border border-transparent'
                        }`}
                        data-testid={`teacher-${teacher.user_id}`}
                      >
                        <Checkbox
                          checked={selectedTeachers.includes(teacher.user_id)}
                          onCheckedChange={() => toggleTeacherSelection(teacher.user_id)}
                        />
                        {teacher.picture ? (
                          <img src={teacher.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{teacher.name}</p>
                          <p className="text-xs text-text-muted truncate">{teacher.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedTeachers.length > 0 && (
                    <p className="text-xs text-neon-green mt-2">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      {selectedTeachers.length} teacher(s) selected
                    </p>
                  )}
                </div>
              )}

              {!shareSchoolWide && teachers.length === 0 && (
                <p className="text-sm text-text-muted text-center py-4">
                  No other teachers registered yet
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => { setShowSaveModal(false); navigate('/teacher'); }}
                  data-testid="skip-save-btn"
                >
                  Skip
                </Button>
                <Button
                  className="flex-1 btn-primary"
                  onClick={handleSaveToLibrary}
                  disabled={saving}
                  data-testid="save-share-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {shareSchoolWide || selectedTeachers.length > 0 ? 'Save & Share' : 'Save to Library'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Template List Component
const TemplateList = ({ templates, selectedTemplate, setSelectedTemplate, form, setForm, creating, selectedClassrooms, handleSubmitFromTemplate, isShared, onDeleteTemplate, deleting, onPrintTemplate }) => (
  <div className="space-y-6">
    {/* Optional Title Override */}
    <div>
      <label className="text-sm text-text-secondary mb-2 block">
        Custom Title (optional - leave blank to use template title)
      </label>
      <Input
        className="input-cyber"
        placeholder="Override template title..."
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        data-testid="template-title-override"
      />
    </div>

    {/* Template List */}
    <div>
      <label className="text-sm text-text-secondary mb-2 block">Select Template *</label>
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {templates.map(template => (
          <div
            key={template.template_id}
            className={`relative p-4 rounded-lg transition-all ${
              selectedTemplate?.template_id === template.template_id
                ? 'bg-secondary/20 border border-secondary/50'
                : 'bg-background-surface hover:bg-white/5 border border-transparent'
            }`}
          >
            <button
              className="w-full text-left"
              onClick={() => setSelectedTemplate(template)}
              data-testid={`template-${template.template_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-8">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{template.title}</p>
                    {/* Exercise Type Badge */}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      template.exercise_type === 'reading' 
                        ? 'bg-secondary/20 text-secondary' 
                        : 'bg-primary/20 text-primary'
                    }`}>
                      {template.exercise_type === 'reading' ? 'Reading Quiz' : 'Typing'}
                    </span>
                    {isShared && (
                      <span className="text-xs px-2 py-0.5 bg-white/10 text-text-muted rounded-full">
                        {template.share_school_wide ? 'School-wide' : `From ${template.teacher_name || 'Teacher'}`}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-primary font-mono mt-1">
                    {template.objectives?.length > 1 
                      ? template.objectives.map(o => o.code).join(', ')
                      : template.objective_code}
                  </p>
                  <p className="text-xs text-text-muted mt-2 line-clamp-2">
                    {template.objectives?.length > 0 
                      ? template.objectives[0]?.description 
                      : template.objective_description}
                  </p>
                </div>
                <div className="text-right text-xs text-text-muted">
                  <p>Grade {template.grade_level}</p>
                  <p className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {template.duration_minutes}m
                  </p>
                </div>
              </div>
              {selectedTemplate?.template_id === template.template_id && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-neon-green flex items-center gap-1 mb-3">
                    <CheckCircle className="w-3 h-3" />
                    Selected - Preview questions below
                  </p>
                  
                  {/* Quiz Questions Preview */}
                  {template.quiz_questions && template.quiz_questions.length > 0 && (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {template.quiz_questions.map((q, qIdx) => (
                        <div key={qIdx} className="p-3 bg-black/20 rounded-lg text-xs">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-medium text-text-primary">
                              {qIdx + 1}. {q.question}
                            </p>
                            {q.objective_code && (
                              <span className="px-2 py-0.5 bg-primary/30 text-primary font-mono rounded flex-shrink-0">
                                {q.objective_code}
                              </span>
                            )}
                          </div>
                          <div className="ml-3 space-y-1 text-text-muted">
                            {q.options?.map((opt, oIdx) => (
                              <p key={oIdx} className={opt.startsWith(q.correct_answer) ? 'text-neon-green font-medium' : ''}>
                                {opt}
                              </p>
                            ))}
                          </div>
                          {(q.hint || q.explanation) && (
                            <div className="mt-2 pt-2 border-t border-white/10">
                              <p className="text-secondary italic">
                                <span className="font-medium">Hint: </span>
                                {(q.hint || q.explanation).substring(0, 150)}{(q.hint || q.explanation).length > 150 ? '...' : ''}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </button>
            
            {/* Action buttons container */}
            <div className="absolute top-3 right-3 flex items-center gap-1">
              {/* Print button - always show */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPrintTemplate(template);
                }}
                className="p-2 rounded-lg hover:bg-primary/20 text-text-muted hover:text-primary transition-colors"
                data-testid={`print-template-${template.template_id}`}
                title="Print passage and questions"
              >
                <Printer className="w-4 h-4" />
              </button>
              
              {/* Delete button - only show for own templates (not shared) */}
              {!isShared && onDeleteTemplate && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete "${template.title}" from your library?`)) {
                      onDeleteTemplate(template.template_id);
                    }
                  }}
                  className="p-2 rounded-lg hover:bg-neon-red/20 text-text-muted hover:text-neon-red transition-colors"
                  disabled={deleting}
                  data-testid={`delete-template-${template.template_id}`}
                  title="Delete from library"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Submit Button */}
    <Button
      className="w-full btn-secondary py-6"
      onClick={handleSubmitFromTemplate}
      disabled={creating || selectedClassrooms.length === 0 || !selectedTemplate}
      data-testid="use-template-btn"
    >
      {creating ? (
        <>
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Creating...
        </>
      ) : (
        <>
          <FolderOpen className="w-5 h-5 mr-2" />
          Use Template for {selectedClassrooms.length} Class{selectedClassrooms.length !== 1 ? 'es' : ''}
        </>
      )}
    </Button>
  </div>
);

export default CreateExercise;
